import { JsonObject } from '../../helpers';
import { CreateBaseModel } from '../base.model';
import { CreateTelemetryModel, PatchTelemetryModel, TelemetryModel } from './telemetry.model';
import { object, string } from 'yup';
import SplunkIcon from '@/assets/telemetry-icons/splunk.png';

export type SplunkTelemetryConfig = {
  hecUrl: string;
  index: string; // Optional target Splunk index
  source: string; // Optional source identifier
  sourceType: string;
  hecToken: string;
  hecTokenLast4: string;
  allowSelfSignedCertificates: boolean;
  indexes: {
    userEvents?: string | null;
    auditEvents?: string | null;
  };
};

export class SplunkTelemetryModel extends TelemetryModel {
  config: SplunkTelemetryConfig;

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof TelemetryModel, unknown>;

    this.type = 'splunk';
    this.typeLabel = 'Splunk HEC Index';
    this.iconImage = SplunkIcon;

    this.config = (data.config as SplunkTelemetryConfig) || {
      hecUrl: '',
      index: '',
      source: '',
      sourceType: '',
      hecToken: '',
      hecTokenLast4: '',
      allowSelfSignedCertificates: false,
      indexes: {
        userEvents: '',
        auditEvents: '',
      },
    };

    this.schema = this.schema.concat(
      object({
        config: object({
          hecUrl: string().required('HEC URL is required'),
          source: string().required('Source Identifier is required'),
          hecToken: string().when('$id', {
            is: (id: string) => !id,
            then: (schema) => schema.required('HEC Token is required'),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      })
    );
  }

  get createModel(): CreateBaseModel {
    return new CreateSplunkTelemetryModel(this);
  }

  get patchModel(): CreateBaseModel {
    return new PatchSplunkTelemetryModel(this);
  }
}

class CreateSplunkTelemetryModel extends CreateTelemetryModel {
  config: Partial<SplunkTelemetryConfig>;

  constructor(telemetry: SplunkTelemetryModel) {
    super(telemetry);

    this.type = 'splunk';
    this.config = telemetry.config;

    delete this.connectorId;
    delete this.config.hecTokenLast4;
    delete this.config.index;
    delete this.config.sourceType;

    if (this.config.indexes) {
      if (!this.config.indexes.userEvents) {
        delete this.config.indexes.userEvents;
      }

      if (!this.config.indexes.auditEvents) {
        delete this.config.indexes.auditEvents;
      }
    }
  }
}

class PatchSplunkTelemetryModel extends PatchTelemetryModel {
  config: Partial<SplunkTelemetryConfig>;

  constructor(telemetry: SplunkTelemetryModel) {
    super(telemetry);

    this.config = telemetry.config;

    delete this.connectorId;
    delete this.config.hecTokenLast4;
    delete this.config.index;
    delete this.config.sourceType;

    if (!this.config.hecToken) {
      delete this.config.hecToken;
    }

    // clearing out an index value requires passing null as opposed to empty string
    if (this.config.indexes) {
      if (!this.config.indexes.userEvents) {
        this.config.indexes.userEvents = null;
      }

      if (!this.config.indexes.auditEvents) {
        this.config.indexes.auditEvents = null;
      }
    }
  }
}
